import React, { useCallback, useEffect, useState } from 'react';
import Modal from '@mui/material/Modal';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { getRequest, postRequest, putRequest, deleteRequest, showMessage } from '../../../config';
import ImageUploader from '../modules/ModuleForm/ImageUploader';

const STAGE_OPTS = ['Investment', 'Growth', 'Exit'];
const SUB_OPTS = ['Free', 'Lite', 'Pro'];
const TARGET_TAB_OPTS = ['Dashboard', 'AnyLists', 'ResearchAid', 'Workflows', 'InvestorMatch'];
const NAVIGATION_TYPE_OPTS = ['Switch Tab', 'Navigation'];

const createStageSubscriptionConfig = () => {
    const list = [];
    STAGE_OPTS.forEach((stage) => {
        SUB_OPTS.forEach((sub) => {
            list.push({
                companyStage: stage,
                companySubscription: sub,
                isVisible: true,
                isRestricted: false,
            });
        });
    });
    return list;
};

const normalizeStageSubscriptionConfig = (raw) => {
    const source = Array.isArray(raw) ? raw : [];
    if (!source.length) {
        return createStageSubscriptionConfig();
    }
    const map = new Map();
    source.forEach((item) => {
        if (!item) return;
        const key = `${item.companyStage}__${item.companySubscription}`;
        map.set(key, {
            companyStage: item.companyStage,
            companySubscription: item.companySubscription,
            isVisible: item.isVisible !== false,
            isRestricted: item.isRestricted === true,
        });
    });
    return createStageSubscriptionConfig().map((entry) => {
        const key = `${entry.companyStage}__${entry.companySubscription}`;
        return map.get(key) || entry;
    });
};

const emptyForm = () => ({
    dashboardCardId: '',
    title: '',
    subTitle: '',
    description: '',
    imageURL: '',
    companyStage: 'All',
    companySubscription: 'All',
    stageSubscriptionConfig: createStageSubscriptionConfig(),
    navigationType: 'Switch Tab',
    targetTab: '',
    externalUrl: '',
    isResearchAid: false,
    menusConfig: [],
    isActive: true,
});

const NavigationModulePage = () => {
    const [items, setItems] = useState([]);
    const [dashboardCards, setDashboardCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm());
    const [expandedMenuKey, setExpandedMenuKey] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [navRes, dashRes] = await Promise.all([
                getRequest('/modules/navigation-module-cards'),
                getRequest('/modules/dashboard-cards'),
            ]);
            if (navRes?.code === 200 && navRes?.body?.items) {
                setItems(navRes.body.items);
            } else {
                setItems([]);
            }
            if (dashRes?.code === 200 && dashRes?.body?.cards) {
                setDashboardCards(dashRes.body.cards);
            } else {
                setDashboardCards([]);
            }
        } catch (e) {
            console.error(e);
            showMessage('Could not load navigation modules.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const openCreate = () => {
        setEditing(null);
        setForm(emptyForm());
        setModalOpen(true);
    };

    const openEdit = (row) => {
        setEditing(row);
        setForm({
            dashboardCardId: row.dashboardCardId?._id || row.dashboardCardId || '',
            title: row.title || '',
            subTitle: row.subTitle || '',
            description: row.description || '',
            imageURL: row.imageURL || '',
            companyStage: row.companyStage || 'All',
            companySubscription: row.companySubscription || 'All',
            navigationType: row.navigationType || (row.externalUrl ? 'Navigation' : 'Switch Tab'),
            targetTab: row.targetTab || '',
            externalUrl: row.externalUrl || '',
            isResearchAid: row.isResearchAid === true,
            stageSubscriptionConfig: normalizeStageSubscriptionConfig(row.stageSubscriptionConfig),
            menusConfig: Array.isArray(row.menusConfig) ? row.menusConfig : [],
            isActive: row.isActive !== false,
        });
        setModalOpen(true);
    };

    const handleClose = () => {
        setModalOpen(false);
        setEditing(null);
        setForm(emptyForm());
        setExpandedMenuKey(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.dashboardCardId || !form.title?.trim()) {
            showMessage('Dashboard card and title are required.');
            return;
        }
        if (form.navigationType === 'Switch Tab' && !form.targetTab?.trim()) {
            showMessage('Target tab is required for Switch Tab.');
            return;
        }
        if (form.navigationType === 'Navigation' && !form.externalUrl?.trim()) {
            showMessage('External URL is required for Navigation.');
            return;
        }
        const payload = {
            dashboardCardId: form.dashboardCardId,
            title: form.title.trim(),
            subTitle: (form.subTitle || '').trim(),
            description: (form.description || '').trim(),
            imageURL: (form.imageURL || '').trim(),
            companyStage: 'All',
            companySubscription: 'All',
            stageSubscriptionConfig: normalizeStageSubscriptionConfig(form.stageSubscriptionConfig),
            navigationType: form.navigationType === 'Navigation' ? 'Navigation' : 'Switch Tab',
            targetTab: form.navigationType === 'Switch Tab' ? form.targetTab.trim() : '',
            externalUrl: form.navigationType === 'Navigation' ? (form.externalUrl || '').trim() : '',
            isResearchAid: form.navigationType === 'Navigation' ? form.isResearchAid === true : false,
            menusConfig: Array.isArray(form.menusConfig) ? form.menusConfig : [],
            isActive: form.isActive,
        };
        if (editing?._id) payload._id = editing._id;

        try {
            const res = editing?._id
                ? await putRequest('/modules/navigation-module-cards', payload)
                : await postRequest('/modules/navigation-module-cards', payload);
            if (res?.code === 200) {
                showMessage(editing ? 'Navigation entry updated.' : 'Navigation entry created.');
                handleClose();
                load();
            } else {
                showMessage(res?.message || 'Save failed');
            }
        } catch (err) {
            console.error(err);
            showMessage('Save failed.');
        }
    };

    const handleDelete = async (row) => {
        if (!window.confirm(`Delete "${row.title}"?`)) return;
        try {
            const res = await deleteRequest(`/modules/navigation-module-cards/${row._id}`);
            if (res?.code === 200) {
                showMessage('Deleted.');
                load();
            } else {
                showMessage(res?.message || 'Delete failed');
            }
        } catch (e) {
            console.error(e);
            showMessage('Delete failed.');
        }
    };

    const dashTitle = (id) => {
        const c = dashboardCards.find((x) => String(x._id) === String(id));
        return c?.title || '—';
    };

    const getVariantCell = (stage, sub) => {
        const list = Array.isArray(form.stageSubscriptionConfig) ? form.stageSubscriptionConfig : [];
        return list.find((x) => x.companyStage === stage && x.companySubscription === sub) || {
            companyStage: stage,
            companySubscription: sub,
            isVisible: false,
            isRestricted: false,
        };
    };

    const updateVariantCell = (stage, sub, patch) => {
        setForm((prev) => {
            const next = normalizeStageSubscriptionConfig(prev.stageSubscriptionConfig).map((x) => {
                if (x.companyStage !== stage || x.companySubscription !== sub) return x;
                return { ...x, ...patch };
            });
            return { ...prev, stageSubscriptionConfig: next };
        });
    };

    const updateAllCells = (patch) => {
        setForm((prev) => ({
            ...prev,
            stageSubscriptionConfig: normalizeStageSubscriptionConfig(prev.stageSubscriptionConfig).map((x) => ({ ...x, ...patch })),
        }));
    };

    const createEmptyMenu = () => {
        const idx = (form.menusConfig || []).length + 1;
        return {
            key: `menu-${Date.now()}-${idx}`,
            label: '',
            iconURL: '',
            isActive: true,
            actionType: 'navigate',
            navigateUrl: '',
            modalDisplayType: 'contentAndImage',
            modalTitle: '',
            modalImageURL: '',
            modalContent: '',
            modalImagePosition: 'right',
        };
    };

    const handleAddMenu = () => {
        const newMenu = createEmptyMenu();
        setForm((f) => ({ ...f, menusConfig: [...(f.menusConfig || []), newMenu] }));
        setExpandedMenuKey(newMenu.key);
    };

    const handleRemoveMenu = (key) => {
        setForm((f) => ({ ...f, menusConfig: (f.menusConfig || []).filter((m) => m.key !== key) }));
        if (expandedMenuKey === key) setExpandedMenuKey(null);
    };

    const handleMenuChange = (key, field, value) => {
        setForm((f) => ({
            ...f,
            menusConfig: (f.menusConfig || []).map((m) => m.key === key ? { ...m, [field]: value } : m),
        }));
    };

    const inputStyle = {
        width: '100%',
        padding: '12px 14px',
        fontSize: '0.95rem',
        border: '1px solid #e0e0e0',
        borderRadius: '10px',
        outline: 'none',
        boxSizing: 'border-box',
        background: '#fff',
    };

    const labelStyle = { display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '0.8rem', color: '#1a2744' };

    return (
        <div className="modules-container navigation-cards-table">
            <div className="modules-header">
                <div>
                    <h1 className="modules-title">Navigation Module</h1>
                    <p className="modules-subtitle">Curate shortcut tiles that appear on the company dashboard.</p>
                </div>
                <button type="button" onClick={openCreate} className="modules-create-btn df aic jcc cp">
                    <AddIcon style={{ fontSize: 18, marginRight: 8 }} />
                    Create Navigation Card
                </button>
            </div>

            <div
                className="modules-table-card"
                style={{
                    borderRadius: '16px',
                    border: '1px solid #e8edf5',
                    background: '#fafbfd',
                    padding: '1.25rem 1.5rem',
                }}
            >
                <div className="df jcsb aic mb1rem" style={{ flexWrap: 'wrap', gap: '8px' }}>
                    <h3 className="modules-table-title" style={{ margin: 0 }}>
                        Entries ({items.length})
                    </h3>
                </div>

                {loading ? (
                    <div className="df jcc aic p4rem">
                        <div className="loader" />
                        <span className="ml1rem fs0-75rem c15">Loading…</span>
                    </div>
                ) : items.length === 0 ? (
                    <div
                        className="tac py4rem"
                        style={{
                            borderRadius: '12px',
                            border: '1px dashed #c7d2fe',
                            background: '#fff',
                            color: '#64748b',
                        }}
                    >
                        <p style={{ margin: 0, fontSize: '1rem' }}>No navigation cards yet. Create one to show it on the dashboard.</p>
                    </div>
                ) : (
                    <div className="modules-table-wrapper">
                        <table className="w100 fs0-75rem c15" style={{ borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid #e0e0e0', background: '#fafafa' }}>
                                    <th className='fs1rem tal' style={{ padding: '12px 16px', width: 72 }}>Image</th>
                                    <th className='fs1rem tal' style={{ padding: '12px 16px' }}>Title</th>
                                    <th className='fs1rem tal' style={{ padding: '12px 16px' }}>Dashboard</th>
                                    <th className='fs1rem tal' style={{ padding: '12px 16px' }}>Behavior</th>
                                    <th className='fs1rem tal' style={{ padding: '12px 16px' }}>Destination</th>
                                    <th className='fs1rem tal' style={{ padding: '12px 16px' }}>Research Aid</th>
                                    <th className='fs1rem tal' style={{ padding: '12px 16px' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((row) => (
                                    <tr key={row._id} style={{ borderBottom: '1px solid #eee' }}>
                                        <td style={{ padding: '12px 16px', textAlign: 'left' }}>
                                            {row.imageURL ? (
                                                <img
                                                    src={row.imageURL}
                                                    alt={row.title || 'Navigation'}
                                                    style={{ width: 34, height: 34, borderRadius: 6, objectFit: 'cover', border: '1px solid #e2e8f0' }}
                                                />
                                            ) : (
                                                '—'
                                            )}
                                        </td>
                                        <td style={{ padding: '12px 16px', textAlign: 'left' }}>
                                            <div className="fw600">{row.title || '—'}</div>
                                            {row.subTitle ? <div className="op70">{row.subTitle}</div> : null}
                                        </td>
                                        <td style={{ padding: '12px 16px', textAlign: 'left' }}>{dashTitle(row.dashboardCardId?._id || row.dashboardCardId)}</td>
                                        <td style={{ padding: '12px 16px', textAlign: 'left' }}>{row.navigationType === 'Navigation' ? 'Navigation' : 'Switch Tab'}</td>
                                        <td style={{ padding: '12px 16px', textAlign: 'left', maxWidth: 280, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {row.navigationType === 'Navigation' ? (row.externalUrl || '—') : (row.targetTab || '—')}
                                        </td>
                                        <td style={{ padding: '12px 16px', textAlign: 'left' }}>{row.isResearchAid ? 'Yes' : 'No'}</td>
                                        <td className='df aic' style={{ padding: '12px 16px', textAlign: 'left' }}>
                                            <button type="button" onClick={() => openEdit(row)} className="df aic jcc cp bg44 c12" style={{ padding: 6, marginRight: 8 }}>
                                                <EditIcon style={{ fontSize: 20 }} />
                                            </button>
                                            <button type="button" onClick={() => handleDelete(row)} className="df aic jcc cp bg44 c12" style={{ padding: 6 }}>
                                                <DeleteIcon style={{ fontSize: 20 }} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <Modal open={modalOpen} onClose={handleClose} className="backdrop td500 zi4 df jcc aic">
                <div
                    className="bg1 shadow br15px bsbb"
                    style={{
                        outline: 'none',
                        maxWidth: '720px',
                        width: '92%',
                        maxHeight: '90vh',
                        overflow: 'auto',
                        padding: '1.75rem',
                        position: 'relative',
                    }}
                >
                    <button
                        type="button"
                        onClick={handleClose}
                        className="pa cp"
                        style={{ top: 12, right: 12, border: 'none', background: 'transparent' }}
                        aria-label="Close"
                    >
                        <CloseIcon />
                    </button>
                    <h2 className="fs1-25rem fw600 c15 mb1rem" style={{ paddingRight: 32 }}>
                        {editing ? 'Edit navigation card' : 'Create navigation card'}
                    </h2>
                    <form onSubmit={handleSubmit}>
                        <label style={labelStyle}>Dashboard card</label>
                        <select
                            style={{ ...inputStyle, marginBottom: 14 }}
                            value={form.dashboardCardId}
                            onChange={(e) => setForm((f) => ({ ...f, dashboardCardId: e.target.value }))}
                            required
                        >
                            <option value="">Select dashboard section…</option>
                            {dashboardCards.map((c) => (
                                <option key={c._id} value={c._id}>
                                    {c.title || c._id}
                                </option>
                            ))}
                        </select>

                        <label style={labelStyle}>Title</label>
                        <input
                            style={{ ...inputStyle, marginBottom: 14 }}
                            value={form.title}
                            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                            required
                        />

                        <label style={labelStyle}>Subtitle</label>
                        <input style={{ ...inputStyle, marginBottom: 14 }} value={form.subTitle} onChange={(e) => setForm((f) => ({ ...f, subTitle: e.target.value }))} />

                        <label style={labelStyle}>Description</label>
                        <textarea
                            style={{ ...inputStyle, minHeight: 72, resize: 'vertical', marginBottom: 14 }}
                            value={form.description}
                            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                        />

                        <div style={{ marginBottom: 14 }}>
                            <ImageUploader
                                imageURL={form.imageURL}
                                onImageChange={(url) => setForm((f) => ({ ...f, imageURL: url }))}
                                label="Navigation card image"
                            />
                        </div>

                        <div style={{ marginBottom: 14 }}>
                            <label style={labelStyle}>Active</label>
                            <select
                                style={inputStyle}
                                value={form.isActive ? 'yes' : 'no'}
                                onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.value === 'yes' }))}
                            >
                                <option value="yes">Yes</option>
                                <option value="no">No</option>
                            </select>
                        </div>

                        <div style={{ marginBottom: 18 }}>
                            <label style={labelStyle}>Visibility Matrix (easy mode)</label>


                            <div className="df fdc" style={{ gap: 10 }}>
                                {STAGE_OPTS.map((stage) => (
                                    <div key={stage} style={{ border: '1px solid #e6edf6', borderRadius: 10, padding: 10, background: '#fbfdff' }}>
                                        <div className="fw600 fs0-85rem mb0-5rem" style={{ color: '#334155' }}>{stage}</div>
                                        <div className="dg" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8 }}>
                                            {SUB_OPTS.map((sub) => {
                                                const cell = getVariantCell(stage, sub);
                                                return (
                                                    <div key={`${stage}-${sub}`} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 8, background: '#fff' }}>
                                                        <div className="fw600 fs0-75rem mb0-5rem">{sub}</div>
                                                        <label className="df aic" style={{ gap: 6, marginBottom: 6 }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={cell.isVisible === true}
                                                                onChange={(e) => updateVariantCell(stage, sub, { isVisible: e.target.checked })}
                                                            />
                                                            <span className="fs0-75rem">is Visible?</span>
                                                        </label>
                                                        <label className="df aic" style={{ gap: 6 }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={cell.isRestricted === true}
                                                                onChange={(e) => updateVariantCell(stage, sub, { isRestricted: e.target.checked })}
                                                            />
                                                            <span className="fs0-75rem">is Restricted?</span>
                                                        </label>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <small className="fs11px c15 op60 mt0-5rem">
                                Turn visibility on/off and mark combinations as restricted.
                            </small>
                        </div>

                        <label style={labelStyle}>Navigation behavior</label>
                        <select
                            style={{ ...inputStyle, marginBottom: 14 }}
                            value={form.navigationType}
                            onChange={(e) => {
                                const nextType = e.target.value === 'Navigation' ? 'Navigation' : 'Switch Tab';
                                setForm((f) => ({
                                    ...f,
                                    navigationType: nextType,
                                    targetTab: nextType === 'Switch Tab' ? f.targetTab : '',
                                    externalUrl: nextType === 'Navigation' ? f.externalUrl : '',
                                }));
                            }}
                        >
                            {NAVIGATION_TYPE_OPTS.map((opt) => (
                                <option key={opt} value={opt}>{opt}</option>
                            ))}
                        </select>

                        {form.navigationType === 'Switch Tab' ? (
                            <>
                                <label style={labelStyle}>Target tab</label>
                                <select
                                    style={{ ...inputStyle, marginBottom: 20 }}
                                    value={form.targetTab}
                                    onChange={(e) => setForm((f) => ({ ...f, targetTab: e.target.value }))}
                                    required
                                >
                                    <option value="">Select target tab...</option>
                                    {TARGET_TAB_OPTS.map((opt) => (
                                        <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                </select>
                            </>
                        ) : (
                            <>
                                <label style={labelStyle}>External URL</label>
                                <input
                                    style={{ ...inputStyle, marginBottom: 20 }}
                                    value={form.externalUrl}
                                    onChange={(e) => setForm((f) => ({ ...f, externalUrl: e.target.value }))}
                                    placeholder="https://…"
                                    required
                                />
                                <label className="df aic" style={{ gap: 8, marginBottom: 20 }}>
                                    <input
                                        type="checkbox"
                                        checked={form.isResearchAid === true}
                                        onChange={(e) => setForm((f) => ({ ...f, isResearchAid: e.target.checked }))}
                                    />
                                    <span className="fs0-8rem">isResearchAid</span>
                                </label>
                            </>
                        )}

                        {/* MENU LIST */}
                        <div style={{ marginBottom: 18, border: '1px solid #e6edf6', borderRadius: 10, padding: 14, background: '#fbfdff' }}>
                            <div className="df jcsb aic mb1rem" style={{ flexWrap: 'wrap', gap: 8 }}>
                                <div>
                                    <div className="fw600 fs0-9rem" style={{ color: '#1a2744' }}>Menu list</div>
                                    <div className="fs0-75rem op60">Configure dynamic menu items (e.g. &quot;How it works?&quot;, &quot;Help&quot;) for this navigation card.</div>
                                </div>
                                <button
                                    type="button"
                                    onClick={handleAddMenu}
                                    className="cp df aic jcc"
                                    style={{ background: 'linear-gradient(135deg, #0d9488, #0f766e)', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: '0.8rem', fontWeight: 600, gap: 4 }}
                                >
                                    <AddIcon style={{ fontSize: 15 }} /> Add menu
                                </button>
                            </div>

                            <div className="df fdc" style={{ gap: 10 }}>
                                {(form.menusConfig || []).length === 0 && (
                                    <p className="fs0-85rem c15 op70 m0">No menus configured yet. Use &quot;Add menu&quot; to create one.</p>
                                )}
                                {(form.menusConfig || []).map((menu) => {
                                    const isExpanded = expandedMenuKey === menu.key;
                                    return (
                                        <div key={menu.key} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 10, background: '#fff' }}>
                                            <button
                                                type="button"
                                                className="df jcsb aic w100 cp"
                                                onClick={() => setExpandedMenuKey(isExpanded ? null : menu.key)}
                                                style={{ background: 'transparent', border: 'none', padding: '4px 0' }}
                                            >
                                                <div className="df aic" style={{ gap: 8 }}>
                                                    <span className="fw500 fs0-9rem c15">{menu.label || 'Untitled menu'}</span>
                                                    {menu.isActive === false && (
                                                        <span className="fs0-7rem br10px px0-5rem py0-15rem" style={{ background: '#ffecec', color: '#c62828' }}>Inactive</span>
                                                    )}
                                                </div>
                                                <div className="df aic" style={{ gap: 6 }}>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => { e.stopPropagation(); handleRemoveMenu(menu.key); }}
                                                        className="cp df aic jcc"
                                                        style={{ border: 'none', background: 'transparent', color: '#c62828' }}
                                                    >
                                                        <DeleteIcon style={{ fontSize: 18 }} />
                                                    </button>
                                                    <span style={{ color: '#666' }}>
                                                        {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                                                    </span>
                                                </div>
                                            </button>

                                            {isExpanded && (
                                                <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                                    {/* Left column */}
                                                    <div className="df fdc" style={{ gap: 10 }}>
                                                        <div>
                                                            <label style={labelStyle}>Menu label</label>
                                                            <input
                                                                style={inputStyle}
                                                                value={menu.label || ''}
                                                                onChange={(e) => handleMenuChange(menu.key, 'label', e.target.value)}
                                                                placeholder="e.g. How It Works?"
                                                            />
                                                        </div>
                                                        <label className="df aic" style={{ gap: 6 }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={menu.isActive !== false}
                                                                onChange={(e) => handleMenuChange(menu.key, 'isActive', e.target.checked)}
                                                            />
                                                            <span className="fs0-8rem fw500">Active menu</span>
                                                        </label>
                                                        <div>
                                                            <label style={labelStyle}>Action type</label>
                                                            <select
                                                                style={inputStyle}
                                                                value={menu.actionType || 'navigate'}
                                                                onChange={(e) => handleMenuChange(menu.key, 'actionType', e.target.value)}
                                                            >
                                                                <option value="navigate">Navigate (open URL)</option>
                                                                <option value="modal">Open modal</option>
                                                            </select>
                                                        </div>
                                                        {menu.actionType === 'navigate' && (
                                                            <div>
                                                                <label style={labelStyle}>Target URL</label>
                                                                <input
                                                                    style={inputStyle}
                                                                    value={menu.navigateUrl || ''}
                                                                    onChange={(e) => handleMenuChange(menu.key, 'navigateUrl', e.target.value)}
                                                                    placeholder="https://example.com/help"
                                                                />
                                                                <small className="fs11px op60">Opens in a new tab.</small>
                                                            </div>
                                                        )}
                                                        {menu.actionType === 'modal' && (
                                                            <>
                                                                <div>
                                                                    <label style={labelStyle}>Modal display type</label>
                                                                    <select
                                                                        style={inputStyle}
                                                                        value={menu.modalDisplayType || 'contentAndImage'}
                                                                        onChange={(e) => handleMenuChange(menu.key, 'modalDisplayType', e.target.value)}
                                                                    >
                                                                        <option value="imageOnly">Image Only</option>
                                                                    </select>
                                                                </div>
                                                                <div>
                                                                    <label style={labelStyle}>Modal title</label>
                                                                    <input
                                                                        style={inputStyle}
                                                                        value={menu.modalTitle || ''}
                                                                        onChange={(e) => handleMenuChange(menu.key, 'modalTitle', e.target.value)}
                                                                        placeholder="Enter modal title"
                                                                    />
                                                                </div>
                                                                <div style={{ marginTop: 4 }}>
                                                                    <ImageUploader
                                                                        imageURL={menu.modalImageURL}
                                                                        onImageChange={(url) => handleMenuChange(menu.key, 'modalImageURL', url)}
                                                                        label="Modal image"
                                                                    />
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                    {/* Right column: icon */}
                                                    <div>
                                                        <ImageUploader
                                                            imageURL={menu.iconURL}
                                                            onImageChange={(url) => handleMenuChange(menu.key, 'iconURL', url)}
                                                            label="Menu icon"
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="df jcfe" style={{ gap: 10 }}>
                            <button type="button" className="cp p1rem br8px bg44" onClick={handleClose}>
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="cp p1rem br8px c1 fw600"
                                style={{ background: 'linear-gradient(135deg, #0d9488, #0f766e)', border: 'none' }}
                            >
                                {editing ? 'Save changes' : 'Create'}
                            </button>
                        </div>
                    </form>
                </div>
            </Modal>
        </div>
    );
};

export default NavigationModulePage;
